/**
 * The C64's software-entity types ($CA,x in rebb64) that the bubble port runs on. The game keeps one
 * type byte per slot and every routine branches on it, so the port does too (`BubbleMotion.code`):
 * types below $24 are bubbles; $34 and up are the burst, the vanish and the blink's invisible half.
 */

/** Shot sizes (D_ACB6, by shot steps left): small, medium, full. $04 is also the plain floating bubble. */
export const SMALL = 0x00;
export const MEDIUM = 0x02;
export const FULL = 0x04;
/** Special bubbles from the holes (D_0CC1). */
export const WATER = 0x06;
export const FIRE = 0x08;
export const LIGHTNING = 0x0a;
/** Letter bubbles: $0C + 2n, n = 0..4 for E X T N D (L_0CA8). */
export const LETTER = 0x0c;
/** Just blown, until the next pass draws it (L2364; D_7BD4 turns it into $00). */
export const JUST_BLOWN = 0x16;
/** Holding a monster: $18 + 2 x its sprite slot on the C64 (L1090); here the monster is in `content`. */
export const TRAPPED = 0x18;
/** The first type that is not a bubble. */
export const NOT_BUBBLE = 0x24;
/**
 * What a burst special bubble becomes (M2, kept in the system's own lists, not as bubbles): a fire drop
 * ($3E5E), a lightning bolt ($3E2C); a red-cross shot (L23B8); the 700-point bubble food ($171B). They
 * run from the entity pass by the jump table at $0D68 ($24 -> L11B7, $26 -> $10D6) or $0D78 ($44 -> $10D3,
 * $4C -> L0EC5).
 */
export const DROP = 0x24;
export const BOLT = 0x26;
export const RED_CROSS_BOLT = 0x44;
export const BUBBLE_FOOD = 0x4c;
/** Burst by a player or a chain: it spreads the chain on the next pass (routine_3D2D). */
export const POPPING = 0x34;
/** Vanishing without a burst: $38, then $36 (D_3DA9, D_7BDB), then the slot is free. */
export const GONE2 = 0x36;
export const GONE = 0x38;
/** Burst without points or chain: timed out, crowded out, level clear (routine_3D77). */
export const QUIET_POP = 0x3a;
/** The burst's frames (routine_3CD3, routine_3CDB, routine_3CDF). */
export const BURST_1 = 0x3c;
export const BURST_2 = 0x3e;
export const BURST_3 = 0x40;
/** A plain bubble's blink, its invisible half: $04 ^ $4C (D_13BE L1428). */
export const HIDDEN = 0x48;
export const BLINK_TOGGLE = 0x4c;

/**
 * D_ACB6, running on into D_ACBD: the type a shot step gives, indexed by the steps left before it
 * (8 steps: small, small, small, medium, medium, medium, full, full).
 */
export const GROW: readonly number[] = [0x10, FULL, FULL, MEDIUM, MEDIUM, MEDIUM, SMALL, SMALL, SMALL];

/** A bubble in the C64's sense (type < $24): trappable, chainable, touchable, bunching. */
export const isBubbleCode = (c: number): boolean => c < NOT_BUBBLE;
/** Holding a monster ($18..$23). */
export const isTrappedCode = (c: number): boolean => c >= TRAPPED && c < NOT_BUBBLE;
/** Water, fire or lightning: the spawner's cap counts types $06..$0B (D_0C56). */
export const isSpecialCode = (c: number): boolean => c >= WATER && c < LETTER;
/** A letter bubble ($0C..$15). */
export const isLetterCode = (c: number): boolean => c >= LETTER && c < JUST_BLOWN;
